import { InjectionToken } from '@angular/core';
import { Observable, of } from 'rxjs';
import type { MessengerNetwork } from '@lite-workspace/dto-user';
import { EMPTY_ONBOARDING_PROGRESS, EMPTY_TEXT_STYLE_MEMORY, parseCreateProjectPreference } from './identity.models';
import type { AuthType, LessonBucket, OnboardingProgress, ProfilePatch, UserProfile } from './identity.models';

export interface IdentityData {
  getProfile(): Observable<UserProfile>;
  updateProfile(patch: ProfilePatch): Observable<UserProfile>;
  setPassword(body: { password?: string; authType?: AuthType }): Observable<void>;
  connectMessenger(network: MessengerNetwork): Observable<string>;
  disconnectMessenger(network: MessengerNetwork): Observable<void>;
  dismissOnboardingFlow(flowId: string): Observable<OnboardingProgress>;
  completeOnboardingFlow(flowId: string): Observable<OnboardingProgress>;
  markLessonViewed(flowId: string): Observable<OnboardingProgress>;
  unmarkLessonViewed(flowId: string): Observable<OnboardingProgress>;
  markLessonFavorite(flowId: string): Observable<OnboardingProgress>;
  unmarkLessonFavorite(flowId: string): Observable<OnboardingProgress>;
}

export const IDENTITY_DATA = new InjectionToken<IdentityData>('IDENTITY_DATA');

/**
 * Host-app channel for surfacing a failure the store recovered from silently. Optional by design:
 * the lib is domain-logic and may not reach a UI toast, so an app that provides nothing simply
 * rolls the change back without a message.
 */
export interface IdentityNotifier {
  notifyError(message: string): void;
}

export const IDENTITY_NOTIFIER = new InjectionToken<IdentityNotifier>('IDENTITY_NOTIFIER');

const INITIAL_MOCK_PROFILE: UserProfile = {
  id: 'u1',
  email: 'sergey.rubins@gmail.com',
  firstName: 'Сергей',
  lastName: 'Сергеев',
  authType: 'password',
  hasPassword: true,
  contacts: [{ name: 'telegram', data: 'sergey_photo', isActive: true }],
  onboardingProgress: EMPTY_ONBOARDING_PROGRESS,
  textStyles: EMPTY_TEXT_STYLE_MEMORY,
  createProjectPreference: null,
};

export class MockIdentityData implements IdentityData {
  private profile: UserProfile = structuredClone(INITIAL_MOCK_PROFILE);

  getProfile(): Observable<UserProfile> {
    return of(structuredClone(this.profile));
  }

  updateProfile(patch: ProfilePatch): Observable<UserProfile> {
    this.profile = {
      ...this.profile,
      ...patch,
      contacts: patch.contacts ?? this.profile.contacts,
      // Server merge mirrored: an absent field keeps its stored value; the reader drops a knobs-only one.
      createProjectPreference:
        patch.createProjectPreference === undefined
          ? this.profile.createProjectPreference
          : parseCreateProjectPreference(
              patch.createProjectPreference && { ...this.profile.createProjectPreference, ...patch.createProjectPreference },
            ),
      retouchPrefs:
        patch.retouchPrefs === undefined
          ? this.profile.retouchPrefs
          : patch.retouchPrefs && { presets: [], compare: 'side', ...this.profile.retouchPrefs, ...patch.retouchPrefs },
    };
    return of(structuredClone(this.profile));
  }

  setPassword(_body: { password?: string; authType?: AuthType }): Observable<void> {
    return of(undefined);
  }

  connectMessenger(network: MessengerNetwork): Observable<string> {
    const start = 'c_00000000000000000000000000000000';
    return of(
      network === 'vk'
        ? `https://vk.me/lumirphoto?ref=${start}&ref_source=connect`
        : `https://t.me/lumirphoto_bot?start=${start}`,
    );
  }

  disconnectMessenger(network: MessengerNetwork): Observable<void> {
    this.profile = { ...this.profile, messengers: { ...this.profile.messengers, [network]: null } };
    return of(undefined);
  }

  dismissOnboardingFlow(flowId: string): Observable<OnboardingProgress> {
    return this.mark('dismissed', flowId);
  }

  completeOnboardingFlow(flowId: string): Observable<OnboardingProgress> {
    return this.mark('completed', flowId);
  }

  markLessonViewed(flowId: string): Observable<OnboardingProgress> {
    return this.mark('viewed', flowId);
  }

  unmarkLessonViewed(flowId: string): Observable<OnboardingProgress> {
    return this.unmark('viewed', flowId);
  }

  markLessonFavorite(flowId: string): Observable<OnboardingProgress> {
    return this.mark('favorites', flowId);
  }

  unmarkLessonFavorite(flowId: string): Observable<OnboardingProgress> {
    return this.unmark('favorites', flowId);
  }

  private mark(bucket: 'completed' | 'dismissed' | LessonBucket, flowId: string): Observable<OnboardingProgress> {
    const progress = this.profile.onboardingProgress;
    const current = progress[bucket] ?? [];
    const ids = current.includes(flowId) ? current : [...current, flowId];
    return this.put({ ...progress, [bucket]: ids });
  }

  private unmark(bucket: LessonBucket, flowId: string): Observable<OnboardingProgress> {
    const progress = this.profile.onboardingProgress;
    return this.put({ ...progress, [bucket]: (progress[bucket] ?? []).filter(id => id !== flowId) });
  }

  private put(onboardingProgress: OnboardingProgress): Observable<OnboardingProgress> {
    this.profile = { ...this.profile, onboardingProgress };
    return of(structuredClone(onboardingProgress));
  }
}
